import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { FragmentOf, readFragment, ResultOf } from "gql.tada";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { GetCaseStudyPageBySlug } from "@queries/GetCaseStudyPageBySlug";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";
import { BLOCKS } from "@contentful/rich-text-types";
import { FootnoteRichText } from "@components/shared/core/FootnoteRichText";
import { Link } from "@components/shared/core/Link";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { TextBlockRichText } from "@components/shared/core/TextBlockRichText";

type Page = NonNullable<
  NonNullable<ResultOf<typeof GetCaseStudyPageBySlug>["caseStudyPageCollection"]>["items"][number]
>;
type Overview = Page["overview"];
type CaseStudyInsights = Page["caseStudyInsights"];
type InsightsBlockItem = FragmentOf<typeof RichEntryFragment> | null;

export const CaseStudyOverviewSection = ({
  overview,
  caseStudyInsights,
  featuredProducts,
}: {
  overview: Overview | null | undefined;
  caseStudyInsights: CaseStudyInsights | null | undefined;
  featuredProducts: FragmentOf<typeof SlideCarouselSectionFragment> | null | undefined;
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeFeaturedProductId, setActiveFeaturedProductId] = useState<string | null>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  // Same shape as DescriptionFillSection: reveals as the section passes through
  // the viewport, finishing by ~30% scroll progress.
  const revealValue = useTransform(scrollYProgress, [0, 0.4], [-1, 0]);

  const blocks =
    caseStudyInsights?.blocksCollection?.items?.filter(
      (b): b is NonNullable<InsightsBlockItem> => b !== null
    ) ?? [];
  const insightsTitle = caseStudyInsights?.title;
  const featuredProductsData = featuredProducts
    ? readFragment(SlideCarouselSectionFragment, featuredProducts)
    : null;
  const featuredProductsItems = featuredProductsData?.itemsCollection?.items?.filter(
    (item): item is NonNullable<typeof item> => item !== null
  ) ?? [];

  return (
    <section
      ref={sectionRef}
      className="py-20 dsk:py-[150px] flex flex-col gap-20 dsk:gap-[150px]"
    >
      {overview && (
        <div className="flex flex-col gap-4 dsk:flex-row dsk:gap-[74px] items-start">
          {overview.title && (
            <p className="text-neutral-cream text-body-large w-full dsk:w-[216px] shrink-0">
              {overview.title}
            </p>
          )}
          {overview.descriptionRichText && (
            <p className="text-palette-current-highlight text-body-feature w-full dsk:flex-1 dsk:max-w-[970px]">
              <AnimatedSplitLines
                value={revealValue}
                animationType="clipPathReveal"
                underlyingTextClassNames="text-palette-current-deep"
              >
                <TextBlockRichText json={overview.descriptionRichText.json} />
              </AnimatedSplitLines>
            </p>
          )}
        </div>
      )}

      {featuredProductsData && (featuredProductsData.title || featuredProductsItems.length > 0) && (
        <div className="flex flex-col gap-4 dsk:flex-row dsk:gap-[74px] items-start">
          {featuredProductsData.title && (
            <p className="text-neutral-cream text-body-large w-full dsk:w-[216px] shrink-0">
              {featuredProductsData.title}
            </p>
          )}
          {featuredProductsItems.length > 0 && (
            <ul className="w-full dsk:flex-1 dsk:max-w-[970px] flex flex-col gap-3">
              {featuredProductsItems.map((item) => {
                const ctaLink = item.ctaLink ? readFragment(LinkFragment, item.ctaLink) : null;
                return ctaLink ? (
                  <li key={item.sys.id}>
                    <Link
                      link={ctaLink}
                      className="group no-underline text-body-feature"
                      onMouseEnter={() => setActiveFeaturedProductId(item.sys.id)}
                      onMouseLeave={() => setActiveFeaturedProductId(null)}
                      onFocus={() => setActiveFeaturedProductId(item.sys.id)}
                      onBlur={() => setActiveFeaturedProductId(null)}
                    >
                      <span className="relative inline-block">
                        <span className="text-palette-current-highlight">{item.title}</span>
                        <motion.span
                          aria-hidden
                          className="absolute inset-0 text-neutral-cream"
                          initial={{ clipPath: "inset(100% 0 0 0)" }}
                          animate={{
                            clipPath:
                              activeFeaturedProductId === item.sys.id
                                ? "inset(0 0 0 0)"
                                : "inset(100% 0 0 0)",
                          }}
                          transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
                        >
                          {item.title}
                        </motion.span>
                      </span>
                    </Link>
                  </li>
                ) : (
                  <li key={item.sys.id} className="text-palette-current-highlight text-body-feature">
                    {item.title}
                  </li>
                );
              })}
            </ul>
          )}
     
        </div>
      )}

      {blocks.length > 0 && (
        <div className="flex flex-col gap-[60px]">
          {insightsTitle && (
            <h2 className="text-heading-3 min-[768px]:text-heading-2 text-balance text-palette-current-highlight">
              {insightsTitle}
            </h2>
          )}
          <ul
            className="flex flex-col dsk:flex-row gap-[10px] w-full"
          >
            {blocks.map((block, i) => (
              <li
                key={i}
                className="bg-neutral-cream/15 rounded-xl p-8 min-h-[400px] flex flex-col items-start flex-1"
                style={{ minWidth: 0 }}
              >
                
                <div className="flex flex-col gap-4 items-start">
                  <FootnoteRichText content={block} options={{
                    renderNode: {
                      [BLOCKS.HEADING_1]: (_, children) => (
                        <p className="text-neutral-cream font-['Netflix_Sans_Title'] font-medium text-[88px] dsk:text-[200px] leading-[0.84] tracking-[-0.03em] capitalize">
                          {children}
                        </p>
                      ),
                      [BLOCKS.PARAGRAPH]: (_, children) => (
                        <p className="text-palette-current-highlight text-body-large max-w-[280px]">
                          {children}
                        </p>
                      ),
                    },
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
     
        </div>
      )}
 
    </section>
  );
};
